import { useState, useRef, useEffect } from "react";

// eslint-disable-next-line no-restricted-imports
import { css, keyframes } from "@emotion/react";
import { useLocalizedHref } from "@libraries/web-framework";
import { useIntl } from "react-intl";

import { media, UNSAFE_media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Image } from "@/components/wpl/image/src";
import { Link } from "@/components/wpl/link/src";
import { DataEventProps } from "@/libraries/wpl/foundations/src";

import { Initializer } from "../../../../analytics/atl-analytics/initializer";

const outerCardStyles = css`
    position: relative;
    display: flex;
    margin: auto;
    max-width: 1000px;
    width: 100%;
    border-radius: 3px;
    box-shadow: 1px 1px 6px -1px #97a0af;
    overflow: hidden;
`;

const cardStyles = css`
    display: flex;
    width: 100%;

    ${media.above.sm} {
        flex-direction: row;
    }

    ${UNSAFE_media.below.sm} {
        flex-direction: column;
    }
`;

const cardSlideInFromLeftAnimation = keyframes`
    from {
        margin-left: -100%;
    }

    to {
        margin-left: 0%;
    }         
`;

const cardSlideInFromRightAnimation = keyframes`
    from {
        margin-left: 100%;
    }

    to {
        margin-left: 0%;
    }         
`;

const cardSlideOutToLeftAnimation = keyframes`
    from {
        margin-left: 0%;
    }

    to {
        margin-left: -100%;
        display: none;
    }         
`;

const cardSlideOutToRightAnimation = keyframes`
    from {
        margin-left: 0%;
    }

    to {
        margin-left: 100%;
        display: none;
    }         
`;

const slidingInStyles = (toRight: boolean) => css`
    position: relative;
    display: flex;
    width: 100%;
    animation: ${toRight
            ? cardSlideInFromRightAnimation
            : cardSlideInFromLeftAnimation}
        350ms ease forwards;
`;

const slidingOutStyles = (toRight: boolean) => css`
    position: absolute;
    top: 0px;
    left: 0px;
    display: flex;
    width: 100%;
    animation: ${toRight
            ? cardSlideOutToLeftAnimation
            : cardSlideOutToRightAnimation}
        350ms ease forwards;
`;

const outerImageStyles = (imageWidth: string) => css`
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #eae6ff;
    padding: ${token("space.500")};

    flex-basis: 33.33%;

    ${UNSAFE_media.below.sm} {
        height: 250px;
        flex-basis: unset;
    }

    img {
        margin: 0;
        max-width: 100%;

        ${UNSAFE_media.below.sm} {
            width: ${imageWidth};
            max-width: 100%;
        }
    }
`;

const buttonStyles = css`
    z-index: 1;
    display: inline-flex;
    position: absolute;
    right: 30px;
    top: 30px;

    button {
        margin-left: 10px;
        width: 30px;
        height: 30px;
        border-radius: 5px;
        border: 0;
        box-shadow: none;
        background-color: #42526e;
        color: #ffffff;
        font-family: var(--bxp-charlie-text-font-family);
        font-size: 1rem;
        text-align: center;
        vertical-align: middle;

        &:hover {
            background-color: #505f79;
            cursor: pointer;
        }
    }
`;

const cardTextStyles = css`
    padding: ${token("space.500")};
    background-color: #ffffff;

    ${UNSAFE_media.above.sm} {
        flex: 1;
    }

    .testimonial-eyebrow-text {
        color: #505f79;
        font-size: 0.75rem;
        font-weight: 400;
        letter-spacing: 0.1rem;
        text-transform: uppercase;
        margin-top: 0;
        margin-bottom: 26px;
    }

    .testimonial-quote-text {
        color: #172b4d;
        font-size: 1.5rem;
        font-weight: 400;
        line-height: 1.33333333;
        margin-top: 0;
        margin-bottom: ${token("space.200")};
        letter-spacing: 0.3px;

        ${UNSAFE_media.below.sm} {
            font-size: 1.25rem;
            line-height: 1.2;
            margin-bottom: 14px;
        }
    }

    .testimonial-quote-credit {
        color: #42526e;
        font-size: 0.875rem;
        font-weight: 700;
        letter-spacing: 1px;
        line-height: 1.25;
        text-transform: uppercase;
        margin-top: 0;
        margin-bottom: 14px;

        ${UNSAFE_media.above.md} {
            margin-bottom: 24px;
        }
    }

    .testimonial-quote-description {
        margin-bottom: ${token("space.300")};
    }
`;

interface TestimonialCardProps {
    cardNumber: string;
    logoImageLink: string;
    logoImageAltText: string;
    imageWidth: string;
    quoteText: string;
    quoteCredit: string;
    quoteDescription: string;
    articleLink: string;
}

const TestimonialCard = ({
    cardNumber,
    logoImageLink,
    logoImageAltText,
    imageWidth,
    quoteText,
    quoteCredit,
    quoteDescription,
    articleLink
}: TestimonialCardProps) => {
    const intl = useIntl();
    const linkText = intl.formatMessage({
        id: "ConfluenceSoftwarePageTemplate.testimonialsSection.storyLink",
        defaultMessage: "Read the story"
    });

    const testimonialCardDataEventProps: DataEventProps = {
        "data-product-key": "confluence",
        "data-event-component": "linkButton",
        "data-event-container": "testimonialsSection",
        "data-label": linkText,
        "data-event": "clicked"
    };
    return (
        <div css={cardStyles}>
            <div css={outerImageStyles(imageWidth)}>
                <Image
                    src={logoImageLink}
                    alt={logoImageAltText}
                    aspectRatio="none"
                />
            </div>
            <div css={cardTextStyles}>
                <h6 className="testimonial-eyebrow-text">{cardNumber}</h6>
                <h3 className="testimonial-quote-text">{quoteText}</h3>
                <h5 className="testimonial-quote-credit">{quoteCredit}</h5>
                <div className="testimonial-quote-description">
                    {quoteDescription}
                </div>
                <Link
                    text={linkText}
                    customLinkColor="b400"
                    doesArrowAnimate={true}
                    type="basic-underline"
                    href={articleLink}
                    dataEventProps={testimonialCardDataEventProps}
                />
            </div>
        </div>
    );
};

export const TestimonialCardsSection = () => {
    const intl = useIntl();
    const [currentCard, setCurrentCard] = useState(0);
    const prevCardRef = useRef<number>(0);
    useEffect(() => {
        prevCardRef.current = currentCard;

        Initializer.autoTrack();
    }, [currentCard]);

    const cards: TestimonialCardProps[] = [
        {
            cardNumber: "1/3",
            logoImageLink:
                "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:31d0af93-6279-456e-ba48-a75903141f5d/Logo_Redfin.svg?cdnVersion=1263",
            logoImageAltText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.testimonialsSection.cardOneImageText",
                defaultMessage: "redfin logo"
            }),
            imageWidth: "215px",
            quoteText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.testimonialsSection.cardOneQuote",
                defaultMessage:
                    "“Confluence is our linchpin for everything. ‘How do I set up my dev box?’ ‘How do I get my work going?’ Whatever they want to know, it’s all there.”"
            }),
            quoteCredit: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.testimonialsSection.cardOneCredit",
                defaultMessage: "Evan Lerer"
            }),
            quoteDescription: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.testimonialsSection.cardOneDescription",
                defaultMessage: "Director of Engineering, Redfin"
            }),
            articleLink: useLocalizedHref("/customers/redfin")
        },
        {
            cardNumber: "2/3",
            logoImageLink:
                "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:0d45de9a-b159-4032-94fd-0ed76b263987/Logo_Hubspot.svg?cdnVersion=1263",
            logoImageAltText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.testimonialsSection.cardTwoImageText",
                defaultMessage: "hubspot logo"
            }),
            imageWidth: "256px",
            quoteText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.testimonialsSection.cardTwoQuote",
                defaultMessage:
                    "“Information is available to every person in the organization from their very first day. HubSpot Confluence pages are open for anyone to discover, read, and comment on.”"
            }),
            quoteCredit: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.testimonialsSection.cardTwoCredit",
                defaultMessage: "Katie Burke"
            }),
            quoteDescription: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.testimonialsSection.cardTwoDescription",
                defaultMessage: "Chief People Officer, Hubspot"
            }),
            articleLink:
                "//atlassian.com/blog/leadership/innovation-transparency-at-work-hubspot"
        },
        {
            cardNumber: "3/3",
            logoImageLink:
                "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:411b2282-6772-4a99-ac9f-ed6f546c8e72/RTR_logo.png?cdnVersion=1265",
            logoImageAltText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.testimonialsSection.cardThreeImageText",
                defaultMessage: "Rent the runway logo"
            }),
            imageWidth: "320px",
            quoteText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.testimonialsSection.cardThreeQuote",
                defaultMessage:
                    '“Confluence has greatly helped with requirements-gathering by allowing easy collaboration with much-appreciated version control, embedding of media, adding comments, and allowing flexible ways to organize and store documents."'
            }),
            quoteCredit: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.testimonialsSection.cardThreeCredit",
                defaultMessage: "Jeannice Angela"
            }),
            quoteDescription: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.testimonialsSection.cardThreeDescription",
                defaultMessage: "Product Manager, Rent the Runway"
            }),
            articleLink: useLocalizedHref("/customers/rent-the-runway")
        }
    ];

    const onClickLeftHandler = () => {
        setCurrentCard((currentCard + 2) % 3);
    };

    const onClickRightHandler = () => {
        setCurrentCard((currentCard + 1) % 3);
    };

    const testimonialSectionCarouselDataEventProps: DataEventProps = {
        "data-product-key": "confluence",
        "data-event-component": "carouselItem",
        "data-event-container": "testimonialsSection",
        "data-event": "clicked"
    };

    return (
        <div css={outerCardStyles}>
            <div css={buttonStyles}>
                <button
                    data-label={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.testimonialsSection.carouselLeft",
                        defaultMessage: "Carousel left click"
                    })}
                    {...testimonialSectionCarouselDataEventProps}
                    onClick={onClickLeftHandler}
                >
                    ←
                </button>
                <button
                    data-label={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.testimonialsSection.carouselRight",
                        defaultMessage: "Carousel right click"
                    })}
                    {...testimonialSectionCarouselDataEventProps}
                    onClick={onClickRightHandler}
                >
                    →
                </button>
            </div>
            {cards.map((cardProps: TestimonialCardProps, index: number) => {
                const toRight =
                    (prevCardRef.current === 0 && currentCard === 1) ||
                    (prevCardRef.current === 1 && currentCard === 2) ||
                    (prevCardRef.current === 2 && currentCard === 0);

                const isIntialRender =
                    prevCardRef.current === 0 && currentCard === 0;

                return (
                    <div
                        key={`card-${index}`}
                        aria-hidden={index !== currentCard}
                    >
                        {isIntialRender && index === currentCard && (
                            <TestimonialCard {...cardProps} />
                        )}
                        {!isIntialRender && (
                            <>
                                {index === currentCard && (
                                    <div css={slidingInStyles(toRight)}>
                                        <TestimonialCard {...cardProps} />
                                    </div>
                                )}
                                {index === prevCardRef.current && (
                                    <div css={slidingOutStyles(toRight)}>
                                        <TestimonialCard {...cardProps} />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const TestimonialsSection = () => {
    return (
        <section
            css={css`
                padding: ${token("space.400")} ${token("space.200")};
                margin: auto;

                ${media.above.md} {
                    width: 83.33333%;
                }
            `}
        >
            <TestimonialCardsSection />
        </section>
    );
};

export default TestimonialsSection;
